import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Activity, Coins, Terminal } from 'lucide-react';
import { toast } from 'sonner';

export default function LogsPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([logsAPI.list(), logsAPI.stats()])
      .then(([logsRes, statsRes]) => { setLogs(logsRes.data); setStats(statsRes.data); })
      .catch(() => toast.error('Failed to load logs'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="logs-page">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center">
          <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
          </Button>
          <span className="ml-4 text-sm text-white/40">/ Generation Logs</span>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold text-white tracking-tight mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}} data-testid="logs-title">
          Generation Logs
        </h1>
        <p className="text-white/40 mb-8">Track all your AI generation activity and token usage</p>

        {/* Summary */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="bg-white/[0.03] border-white/10" data-testid="log-stat-total-tokens">
              <CardContent className="p-5 flex items-center gap-4">
                <Coins className="w-8 h-8 text-amber-400/50" />
                <div>
                  <p className="text-2xl font-bold text-amber-400">{stats.total_tokens_spent}</p>
                  <p className="text-xs tracking-wider uppercase text-white/30">Total Tokens Spent</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/[0.03] border-white/10" data-testid="log-stat-actions">
              <CardContent className="p-5 flex items-center gap-4">
                <Activity className="w-8 h-8 text-indigo-400/50" />
                <div>
                  <p className="text-2xl font-bold text-white">{logs.length}</p>
                  <p className="text-xs tracking-wider uppercase text-white/30">Total Actions</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-5">
                <p className="text-xs tracking-wider uppercase text-white/30 mb-2">By Action Type</p>
                <div className="flex flex-wrap gap-2">
                  {stats.stats?.map((s, i) => (
                    <Badge key={i} variant="outline" className="text-white/50 border-white/10 text-xs">
                      {s._id}: {s.count} ({s.total_tokens} tkn)
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="bg-white/[0.03] border-white/10">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/40">Time</TableHead>
                <TableHead className="text-white/40">Action</TableHead>
                <TableHead className="text-white/40">Phase</TableHead>
                <TableHead className="text-white/40">Tokens</TableHead>
                <TableHead className="text-white/40">Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} className="border-white/10 hover:bg-white/[0.02]" data-testid={`log-entry-${log.id}`}>
                  <TableCell className="text-white/30 text-xs">{new Date(log.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-white/40 border-white/10 text-[10px]">{log.action}</Badge>
                  </TableCell>
                  <TableCell className="text-white/40 text-sm">{log.phase}</TableCell>
                  <TableCell className="text-amber-400 font-mono text-sm">{log.tokens_spent}</TableCell>
                  <TableCell className="text-white/30 text-xs max-w-[300px] truncate">{log.message_preview}</TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-white/30 py-12">No generation logs yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
